import GSAP from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

GSAP.registerPlugin(ScrollTrigger);

export default class PinSwapper {
  constructor(lenis) {
    this.lenis = lenis;
    this.setupDesktop = this.setupDesktop.bind(this);
    this.showImage = this.showImage.bind(this);
    this.destroy = this.destroy.bind(this);

    this.mm = null;
    this.triggers = [];
    this.clickHandlers = [];
    this.currentSection = null;
    this.hoverTimeout = null;
  }

  setupSection(section) {
    if (!section) return;

    this.destroy();

    ScrollTrigger.config({
      ignoreMobileResize: true,
    });

    requestAnimationFrame(() => {
      this.currentSection = section;
      this.mm = GSAP.matchMedia();

      // 🔥 FIX: Run setupDesktop for ALL devices so images initialize
      this.setupDesktop(section);

      requestAnimationFrame(() => {
        window.scrollTo(window.scrollX, 0);
        ScrollTrigger.refresh(true);
      });
    });
  }

  setupDesktop(section) {
    const wrapper = section.closest('.menu__wrapper') || section;
    const itemsContainer = section.querySelector('.menu__item__container');
    const images = Array.from(section.querySelectorAll('.menu__image'));
    const items = Array.from(section.querySelectorAll('.menu__item'));
    const isMobile = window.innerWidth <= 1042; // Match your SCSS breakpoint

    if (itemsContainer) {
      GSAP.set(itemsContainer, { y: 0 });
    }

    const h = itemsContainer.scrollHeight;
    const scrollDistance = h - window.innerHeight + 500;

    // --- 1. PINNING LOGIC (DESKTOP ONLY) ---
    if (!isMobile && items.length > 0) {
      const pinTrigger = ScrollTrigger.create({
        trigger: '.navigation',
        start: 'top top',
        end: () => (items.length <= 4 ? '+=200' : `+=${scrollDistance}`),
        pin: wrapper,
        immediateRender: true,
        anticipatePin: 1,
        pinType: 'transform',
        onRefresh: self => {
          GSAP.set(itemsContainer, { y: 0 });
          self.scroll(0);
        },
      });
      this.triggers.push(pinTrigger);

      if (items.length > 4 && scrollDistance > 0) {
        const scrollEffectTL = GSAP.timeline({
          scrollTrigger: {
            trigger: '.navigation',
            start: 'top top',
            end: () => `+=${scrollDistance}`,
            scrub: true,
          },
        });

        scrollEffectTL.to(itemsContainer, {
          y: -scrollDistance,
          ease: 'none',
        });

        this.triggers.push(scrollEffectTL.scrollTrigger);
      }
    }

    // --- 2. IMAGE SWAPPING (UNIVERSAL) ---
    this.mm.add('(min-width: 1043px)', () => {
      items.forEach((item, i) => {
        const enterHandler = () => {
          if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
          this.hoverTimeout = setTimeout(() => {
            this.showImage(images, i);
          }, 100);
        };
        item.addEventListener('mouseenter', enterHandler);
        this.clickHandlers.push({
          el: item,
          handler: enterHandler,
          event: 'mouseenter',
        });
        if (i === 0) this.showImage(images, i, true);
      });
    });

    this.mm.add('(max-width: 1042px)', () => {
      items.forEach((item, i) => {
        const clickHandler = () => {
          this.showImage(images, i);

          // 🔥 Use Lenis for smooth scrolling back to the image on mobile
          if (this.lenis) {
            this.lenis.scrollTo(wrapper, {
              offset: -120, // Adjust this so the sticky header doesn't cover the image
              duration: 1.2,
              easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Smooth "out" curve
            });
          }
        };

        item.addEventListener('click', clickHandler);
        this.clickHandlers.push({
          el: item,
          handler: clickHandler,
          event: 'click',
        });

        if (i === 0) this.showImage(images, i, true);
      });
    });
  }

  showImage(images, index, instant = false) {
    if (!images[index]) return;
    const targetImage = images[index];
    if (targetImage.classList.contains('is-active') && !instant) return;

    images.forEach(img => {
      if (img.classList.contains('is-active')) {
        GSAP.set(img, { zIndex: 1 });
        img.classList.remove('is-active');
      } else {
        GSAP.set(img, { zIndex: 0 });
      }
    });

    targetImage.classList.add('is-active');

    if (instant) {
      GSAP.set(targetImage, {
        zIndex: 2,
        clipPath: 'inset(0% 0% 0% 0%)',
        autoAlpha: 1,
      });
      return;
    }

    GSAP.killTweensOf(targetImage);
    GSAP.set(targetImage, {
      zIndex: 2,
      autoAlpha: 1,
      clipPath: 'inset(100% 0% 0% 0%)',
    });
    GSAP.to(targetImage, {
      clipPath: 'inset(0% 0% 0% 0%)',
      duration: 0.8,
      ease: 'expo.out',
      overwrite: 'auto',
    });
  }

  destroy() {
    this.triggers.forEach(t => t && t.kill());
    this.triggers = [];
    const container = document.querySelector('.menu__item__container');
    const wrapper = this.currentSection
      ? this.currentSection.closest('.menu__wrapper') || this.currentSection
      : null;

    if (container) {
      GSAP.killTweensOf(container);
      GSAP.set(container, { clearProps: 'all' });
    }
    if (wrapper) {
      GSAP.killTweensOf(wrapper);
      GSAP.set(wrapper, { clearProps: 'all' });
    }

    this.mm && this.mm.revert();
    this.clickHandlers.forEach(({ el, handler, event }) => {
      el.removeEventListener(event, handler);
    });
    this.clickHandlers = [];
    this.currentSection = null;
    ScrollTrigger.refresh(true);
  }
}
